
import React from 'react';
import { Play, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FloatingActionButtonsProps {
  onCreateTask: () => void;
  onStartFocus?: () => void;
  showStartFocus?: boolean;
  splitMode?: boolean;
  className?: string;
  onShowTaskLimitNudge?: () => void;
  canCreateTask?: boolean;
}

const FloatingActionButtons: React.FC<FloatingActionButtonsProps> = ({
  onCreateTask,
  onStartFocus,
  showStartFocus = false,
  splitMode = false,
  className,
  onShowTaskLimitNudge,
  canCreateTask = true
}) => {
  const handleStartFocus = () => {
    if (onStartFocus) {
      onStartFocus();
    }
    
    // Dispatch the startFocusTime event to trigger the timer
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('startFocusTime'));
    }
  };

  const handleCreateTask = () => {
    console.log('FloatingActionButtons: Create task clicked, canCreateTask:', canCreateTask);
    if (!canCreateTask) {
      console.log('FloatingActionButtons: Showing task limit nudge');
      onShowTaskLimitNudge?.();
      return;
    }
    onCreateTask();
  };

  // FAB (single): Only "Create New Task"
  if (!splitMode) {
    return (
      <div className={cn(
        "fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 flex",
        className
      )}>
        <Button
          onClick={handleCreateTask}
          className={cn(
            "h-14 px-8 rounded-full shadow-lg bg-white border-2 border-[#0F172A] text-[#0F172A] text-base font-semibold flex items-center gap-2 min-w-[224px] transition-all dark:bg-[#0F172A] dark:text-white dark:border-[#0F172A]",
            // Improved hover (light: slate-100, dark: slate-700)
            "hover:bg-slate-50 dark:hover:bg-slate-700"
          )}
          aria-label="Create new task"
        >
          <Plus className="h-5 w-5" />
          <span>Create new task</span>
        </Button>
      </div>
    );
  }

  // FAB (split): visually two halves, left is START, right is CREATE
  return (
    <div className={cn(
      "fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 flex",
      className
    )}>
      <div className="flex w-[328px] h-14 rounded-full shadow-xl overflow-hidden bg-transparent border-none">
        {/* Left: Start */}
        <button
          onClick={handleStartFocus}
          className={cn(
            "flex-1 flex gap-2 items-center justify-center h-full font-semibold text-base transition-colors focus:outline-none rounded-l-full",
            // Light: bg-[#0F172A] text-white hover solid, dark: bg-white text-[#0F172A] hover solid
            "bg-[#0F172A] text-white hover:bg-slate-700",
            "dark:bg-white dark:text-[#0F172A] dark:hover:bg-gray-100"
          )}
          style={{ borderRight: '1.5px solid #E5E7EB' }}
          aria-label="Start focus time"
        >
          <Play className="h-5 w-5" />
          <span>Start</span>
        </button>
        {/* Right: Create */}
        <button
          onClick={handleCreateTask}
          className={cn(
            "flex-1 flex gap-2 items-center justify-center h-full font-semibold text-base transition-colors focus:outline-none rounded-r-full",
            "bg-white text-[#0F172A] border-t border-b border-r border-[#0F172A] hover:bg-slate-50",
            // Dark: solid dark bg, solid white border, hover darker slate for clarity
            "dark:bg-[#0F172A] dark:text-white dark:border-white dark:border-t dark:border-b dark:border-r dark:hover:bg-slate-700"
          )}
          aria-label="Create new task"
          style={{
            borderLeft: "none"
          }}
        >
          <Plus className="h-5 w-5" />
          <span>Create new</span>
        </button>
      </div>
    </div>
  );
};

export default FloatingActionButtons;
